import { useRef, useCallback, useContext } from 'react';
import { Copy, Star } from 'react-feather';
import { PopperMenu, PopperMenuItem } from '@trinity/chat-components/components/Popper/index.js';
import { FloatingWrapper } from '@/components/Remirror';
import { useElementBoundingRect } from '@trinity/chat-components/hooks/useElementBoundingRect';
import { TranscriptEditorRefContext, MenuColorDisplayContext } from './context';
import {
  useDownloadHighlight,
  useMarkGoodQuote,
  useCopyHighlightedText,
  useHighlightTooltipPositioner,
  useHelpers,
  useAddTag,
  useAddNewComment,
  useRedactHighlight,
  useTextReplaceHighlight,
  useHandleReassignSpeaker,
  useTooltipHighlightConfig,
} from './hooks';
import styles from './style/Highlight.Menu.module.css';
import { StyledPopper, classes } from './StyledPopper';
import { Tagging } from '../icons/Tagging';

const ICON_SIZE = 22;

type Props = {
  onRemove: () => void;
};

export const Menu = ({ onRemove }: Props) => {
  const position = useHighlightTooltipPositioner();
  const containerRef = useRef<HTMLDivElement>(null);
  const editor = useContext(TranscriptEditorRefContext);

  const config = useTooltipHighlightConfig();

  const helpers = useHelpers();
  const highlight = helpers.getFocusedHighlight();
  const markGoodQuote = useMarkGoodQuote(highlight);

  const [, setColorTooltipShown] = useContext(MenuColorDisplayContext);

  const copyText = useCopyHighlightedText();
  const addTag = useAddTag(highlight);
  const addComment = useAddNewComment(highlight);
  const redactHighlight = useRedactHighlight();
  const textReplaceHighlight = useTextReplaceHighlight();
  const reassignSpeaker = useHandleReassignSpeaker();
  const downloadClip = useDownloadHighlight(highlight);

  const handleRedact = useCallback(() => {
    redactHighlight(highlight);
  }, [highlight, redactHighlight]);

  const handleTextReplaceHighlight = useCallback(() => {
    textReplaceHighlight(highlight);
  }, [highlight, textReplaceHighlight]);

  const handleReassignSpeaker = useCallback(() => {
    reassignSpeaker(highlight);
  }, [highlight, reassignSpeaker]);

  const handleShowColors = useCallback(() => {
    setColorTooltipShown(true);
  }, [setColorTooltipShown]);

  const editorBoundingRect = useElementBoundingRect(editor);

  const leftPos = editorBoundingRect ? editorBoundingRect.left + editorBoundingRect.width : null;

  return (
    <FloatingWrapper
      position={position}
      containerRef={containerRef}
      renderOutsideEditor={true}
      positionOverrides={{ left: leftPos }}
    >
      <StyledPopper
        id="highlight-menu"
        className={classes.popper}
        container={containerRef.current}
        placement="right"
        open={position.visible}
        anchorEl={position.element}
        style={{
          zIndex: 2,
        }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 15],
            },
          },
        ]}
      >
        <PopperMenu>
          {config.tagging && !highlight.dbId && (
            <PopperMenuItem className={styles.menuItem} onClick={markGoodQuote}>
              <Star size={ICON_SIZE} />
              Mark Good Quote
            </PopperMenuItem>
          )}
          {config.tagging && (
            <PopperMenuItem className={styles.menuItem} onClick={addTag}>
              <Tagging size={ICON_SIZE} />
              Add Tag
            </PopperMenuItem>
          )}
          <PopperMenuItem className={styles.menuItem} onClick={copyText}>
            <Copy size={ICON_SIZE} />
            Copy Text
          </PopperMenuItem>
        </PopperMenu>
      </StyledPopper>
    </FloatingWrapper>
  );
};
